import { useRef, useState, type ChangeEvent } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { InfoMessage } from "ui/info-message";
import { TextArea } from "ui/text-area";
import { Typography } from "ui/typography";
import toast from "components/partials/toast/toast";
import CollegeProfileEditActions from "containers/admin/clients/client/college-profile-page/college-profile-edit-actions";
import CollegeProfileSectionHeader from "containers/admin/clients/client/college-profile-page/college-profile-section-header";

import { useCurrentUser } from "hooks/api/use-current-user";
import {
  useSetSystemConfiguration,
  useSystemConfiguration,
} from "hooks/api/use-client-configurations";
import { useUnsavedChangesGuard } from "hooks/use-unsaved-changes-guard";

import { getErrorMessage } from "utilities";
import { hasPermission } from "utilities/user";

import { ClientConfigurationDTOConfigKey } from "generated/schemas";
import { Permission } from "types/auth";

const CONFIG_KEY = ClientConfigurationDTOConfigKey.CAMPAIGN_WRITER_EMAIL_SYSTEM_PROMPT;

const PANEL_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  height: "100%",
  p: 2,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  bgcolor: "common.white",
};

const RULES_SX: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  maxWidth: "736px",
  overflowY: "auto",
  whiteSpace: "pre-wrap",
};

const EDIT_AREA_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  maxWidth: "736px",
  gap: 1,
};

const TEXT_AREA_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  "& .MuiOutlinedInput-root": { flex: 1, alignItems: "stretch" },
  "& textarea": { height: "100%" },
};

export const CampaignWriterRulesPage = () => {
  const currentUser = useCurrentUser();
  const canEdit = hasPermission(currentUser, Permission.PERM_SUPER_ADMIN);

  const { data: rulesConfiguration, isLoading } = useSystemConfiguration(CONFIG_KEY);
  const setSystemConfiguration = useSetSystemConfiguration();

  const rulesValue = rulesConfiguration?.configValue ?? "";

  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(rulesValue);
  const editStartValueRef = useRef(rulesValue);

  const isDraftDirty = draftValue !== editStartValueRef.current;

  const { handleCancelClick, unsavedChangesDialog } = useUnsavedChangesGuard({
    isEditing,
    isDirty: isDraftDirty,
    onDiscard: () => setIsEditing(false),
  });

  const handleEdit = () => {
    editStartValueRef.current = rulesValue;
    setDraftValue(rulesValue);
    setIsEditing(true);
  };

  const handleDraftValueChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDraftValue(event.target.value);
  };

  const handleSave = async () => {
    if (!draftValue.trim()) {
      return;
    }

    try {
      await setSystemConfiguration.mutateAsync({ configKey: CONFIG_KEY, configValue: draftValue });
      setIsEditing(false);
      toast.success({ data: { title: "Updated Campaign Writer rules" } });
    } catch (error) {
      toast.error({
        data: {
          title: "Failed to update Campaign Writer rules",
          message: getErrorMessage(error?.response?.data),
        },
      });
    }
  };

  return (
    <>
      {unsavedChangesDialog}
      <Box sx={PANEL_SX}>
        <CollegeProfileSectionHeader title="Campaign Writer rules">
          {canEdit && (
            <CollegeProfileEditActions
              isEditing={isEditing}
              isSaving={setSystemConfiguration.isPending}
              isEditDisabled={isLoading}
              saveDisabled={!draftValue.trim()}
              onEdit={handleEdit}
              onCancel={handleCancelClick}
              onSave={handleSave}
            />
          )}
        </CollegeProfileSectionHeader>

        <InfoMessage color="info">Rules will be applied during content generation.</InfoMessage>

        {isEditing ? (
          <Box sx={EDIT_AREA_SX}>
            <TextArea
              value={draftValue}
              onChange={handleDraftValueChange}
              disableAutosize
              sx={TEXT_AREA_SX}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            >
              Specify formatting requirements, tone of voice, key messaging, content structure, or any
              specific guidelines.
            </Typography>
          </Box>
        ) : (
          <Typography variant="body1" color="secondary.main" sx={RULES_SX}>
            {isLoading ? "Loading..." : rulesValue || "There are no rules yet."}
          </Typography>
        )}
      </Box>
    </>
  );
};
